import type { AdminStoryStatus, JobStatus } from '@fonus/shared';
import { type ReactNode, useLayoutEffect, useRef, type TextareaHTMLAttributes } from 'react';
import { STATUS_LABEL } from '../lib/format.ts';
import { cn } from '../lib/utils.ts';
import { Badge } from './ui/badge.tsx';
import { Button } from './ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from './ui/dialog.tsx';
import { Skeleton } from './ui/skeleton.tsx';

export function PageTitle({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-[28px] font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-[15px] text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-[var(--radius-card)] border border-dashed border-line px-6 py-12 text-center">
      {icon && <div className="mb-1 text-accent [&_svg]:size-8">{icon}</div>}
      <p className="text-[17px] font-semibold">{title}</p>
      <p className="max-w-md text-[14px] text-muted">{hint}</p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export const ListSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <div className="flex flex-col gap-3" aria-label="Загрузка">
    {Array.from({ length: rows }, (_, i) => (
      <Skeleton key={i} className="h-20 w-full" />
    ))}
  </div>
);

const STORY_TONE: Record<AdminStoryStatus, 'neutral' | 'accent' | 'ok' | 'warn' | 'bad'> = {
  DRAFT: 'neutral',
  IN_REVIEW: 'accent',
  SCHEDULED: 'warn',
  PUBLISHED: 'ok',
  ARCHIVED: 'neutral',
};
export const StoryStatusBadge = ({ status }: { status: AdminStoryStatus }) => (
  <Badge tone={STORY_TONE[status]}>{STATUS_LABEL[status]}</Badge>
);

const JOB: Record<JobStatus, [string, 'neutral' | 'accent' | 'ok' | 'warn' | 'bad']> = {
  QUEUED: ['в очереди', 'neutral'],
  RUNNING: ['идёт', 'accent'],
  SUCCEEDED: ['готово', 'ok'],
  FAILED: ['ошибка', 'bad'],
  CANCELLED: ['отменено', 'neutral'],
};
export const JobStatusBadge = ({ status, stopping }: { status: JobStatus; stopping?: boolean }) => {
  const [label, tone] = JOB[status];
  return <Badge tone={tone}>{stopping ? 'останавливается…' : label}</Badge>;
};

export function Confirm({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive,
  onConfirm,
  busy,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
  busy?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription asChild>
          <div>{description}</div>
        </DialogDescription>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Grows with its content (no inner scroll), per the owner's editor request. */
export function AutoTextarea({
  className,
  value,
  inputRef,
  ...p
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  value: string;
  /** For callers that need the cursor position (tag insertion). */
  inputRef?: { current: HTMLTextAreaElement | null };
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${String(el.scrollHeight + 2)}px`;
  }, [value]);
  return (
    <textarea
      ref={(el) => {
        ref.current = el;
        if (inputRef) inputRef.current = el;
      }}
      value={value}
      rows={1}
      className={cn(
        'w-full resize-none overflow-hidden rounded-[14px] border border-line bg-card px-4 py-3 text-[16px] leading-relaxed outline-none focus:border-accent disabled:opacity-60',
        className,
      )}
      {...p}
    />
  );
}

export const Field = ({
  label,
  hint,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <label className={cn('flex flex-col gap-1.5', className)}>
    <span className="text-[13px] font-semibold text-muted">{label}</span>
    {children}
    {hint && <span className="text-[12px] text-muted">{hint}</span>}
  </label>
);

export const inputClass =
  'h-10 w-full rounded-[12px] border border-line bg-card px-3 text-[15px] outline-none focus:border-accent disabled:opacity-60';
